/**
 * Edit Patient Profile Page Component
 *
 * Allows patients to edit their existing profile information.
 * Pre-populates form with current profile data and handles updates.
 *
 * @module components/pages/EditPatientProfilePage
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { userDomain, UserApiError } from '@/services/domains/UserDomain';
import { PatientProfileForm } from '@/components/patient-registration/PatientProfileForm';
import type { PatientProfileFormData } from '@/types/patientRegistration';
import { TitleText } from '@/components/ui/TitleText';
import { BodyText } from '@/components/ui/BodyText';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useTheme } from '@/hooks/useTheme';
import type {
  PatientProfileApiRequest,
  PatientProfileApiResponse
} from '@/types/patientRegistration';
import { Layout } from '../layout';
import { FileUtils } from '@/utils/fileUtils';
import { getLogger } from '@/services/shared/LoggerFactory';
import { BackButton } from '../ui/BackButton';

/**
 * Transform API response to form data format
 */
const transformApiToFormData = (apiData: PatientProfileApiResponse): Partial<PatientProfileFormData> => {
  return {
    firstName: apiData.first_name || '',
    lastName: apiData.last_name || '',
    address: {
      street: apiData.address?.address || '',
      city: apiData.address?.city || '',
      state: apiData.address?.state || '',
      postal_code: apiData.address?.postal_code || '',
      country: apiData.address?.country || 'AU'
    },
    phone: {
      number: (() => {
        // Handle phone data from API - remove +61 prefix if present
        if(apiData.phone && typeof apiData.phone === 'object' ) {
          return apiData.phone.number ?? '';
        }
        const phoneNumber = apiData.phone as string;
        return phoneNumber?.startsWith('+61') ? '0' + phoneNumber!.slice(3) : phoneNumber;
      })(),
      country_code: (() => {
        // Only extract country_code if phone is an object
        if (apiData.phone && typeof apiData.phone === 'object' && 'country_code' in apiData.phone) {
          return apiData.phone.country_code || '61';
        }
        return '61'; // Default to Australia
      })(),
      iso_code: (() => {
        // Only extract iso_code if phone is an object
        if (apiData.phone && typeof apiData.phone === 'object' && 'iso_code' in apiData.phone) {
          return apiData.phone.iso_code || 'AU';
        }
        return 'AU'; // Default to Australia
      })()
    },
    date_of_birth: apiData.date_of_birth || '',
    // Note: photo handling would need additional implementation for existing photos
  };
};

/**
 * Transform form data to API request format
 */
const transformFormToApiData = async (formData: PatientProfileFormData): Promise<PatientProfileApiRequest> => {
  let photoData = undefined;
  if(formData.photo) {
    photoData = await FileUtils.safeConvertToBase64(formData.photo)
  }

  return {
    first_name: formData.firstName,
    last_name: formData.lastName,
    date_of_birth: formData.date_of_birth,
    address: {
      address: formData.address.street,
      city: formData.address.city,
      state: formData.address.state,
      postal_code: formData.address.postal_code,
      country: formData.address.country,
    },
    phone: {
      number: formData.phone.number,
      country_code: formData.phone.country_code,
      iso_code: formData.phone.iso_code,
    },
    photo: photoData,
    consent: {
      terms_of_service: true,
      privacy_policy: true,
      data_processing: true,
      biometric_processing: true,
      health_data_processing: true,
    },
  };
};

/**
 * Edit Patient Profile Page Component
 */
export const EditPatientProfilePage: React.FC = () => {
  const logger = getLogger();
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Profile state
  const [profile, setProfile] = useState<PatientProfileApiResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [profileError, setProfileError] = useState<Error | null>(null);

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitErrors, setSubmitErrors] = useState<Record<string, string>>({});
  const [initialFormData, setInitialFormData] = useState<Partial<PatientProfileFormData> | undefined>();

  // Fetch profile data
  const fetchProfile = useCallback(async () => {
    setIsLoading(true);
    setProfileError(null);

    try {
      const profileData = await userDomain.getPatientProfile();
      setProfile(profileData);
      setInitialFormData(transformApiToFormData(profileData));
    } catch (error) {
      logger.error('Failed to fetch patient profile:', error);
      // setProfileError(error as Error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load profile on component mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (formData: PatientProfileFormData) => {
    setIsSubmitting(true);
    setSubmitErrors({});

    try {
      // We need the current name from the profile
      if (!profile) {
        throw new Error('Profile data not available');
      }

      // Transform form data to API format
      const apiData = await transformFormToApiData(formData);

      // Update profile
      const response = await userDomain.updatePatientProfile(apiData);

      if (response) {
        // Refresh profile data to get updated information
        await fetchProfile();

        // Navigate back to dashboard
        navigate('/dashboard');
      } else {
        setSubmitErrors({ form: 'Failed to update profile. Please try again.' });
      }
    } catch (error) {
      logger.error('Profile update error:', error);

      if (error instanceof UserApiError) {
        if (error.fieldErrors) {
          setSubmitErrors(error.fieldErrors);
        } else {
          setSubmitErrors({ form: error.message });
        }
      } else {
        setSubmitErrors({ form: 'An unexpected error occurred. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [profile, fetchProfile, navigate]);

  /**
   * Handle cancel action
   */
  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen p-6" data-testid="page-container">
        <div className="max-w-2xl mx-auto" data-testid="inner-container">
          <Card className="p-6">
            <div className="animate-pulse space-y-6" data-testid="loading-skeleton" aria-label="Loading profile information">
              <div className="h-8 bg-neutral-300 rounded w-1/2" data-testid="skeleton-title"></div>
              <div className="h-4 bg-neutral-300 rounded w-3/4" data-testid="skeleton-subtitle"></div>
              <div className="space-y-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-12 bg-neutral-300 rounded" data-testid={`skeleton-field-${i}`}></div>
                ))}
              </div>
              <div className="flex gap-4">
                <div className="h-10 bg-neutral-300 rounded w-24" data-testid="skeleton-button-1"></div>
                <div className="h-10 bg-neutral-300 rounded w-32" data-testid="skeleton-button-2"></div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Error state
  if (profileError) {
    return (
      <div className="min-h-screen p-6" data-testid="page-container">
        <div className="max-w-2xl mx-auto" data-testid="inner-container">
          <Card className="p-6">
            <div className="text-center space-y-4">
              <div className={`p-4 rounded-lg ${
                isDark ? 'bg-error-900/20 text-error-400' : 'bg-error-50 text-error-600'
              }`} data-testid="error-container">
                <TitleText level={3} className="mb-2">
                  Unable to Load Profile
                </TitleText>
                <BodyText>
                  {profileError.message || 'Failed to load your profile information.'}
                </BodyText>
              </div>
              <div className="flex gap-4 justify-center">
                <Button variant="outline" onClick={() => fetchProfile()}>
                  Try Again
                </Button>
                <Button variant="outline" onClick={handleCancel}>
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // No profile data
  if (!profile || !initialFormData) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-2xl mx-auto">
          <Card className="p-6">
            <div className="text-center space-y-4">
              <TitleText level={3}>
                Profile Not Found
              </TitleText>
              <BodyText variant="muted">
                No profile information was found. Please complete your registration first.
              </BodyText>
              <Button variant="outline" onClick={handleCancel}>
                Back to Dashboard
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen p-6" data-testid="page-container">
        <div className="max-w-2xl mx-auto" data-testid="inner-container">
          <div className="flex items-center gap-4 mb-4">
            <BackButton fallback='/dashboard'/>
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <TitleText level={1} className="mb-2">
              Edit Profile
            </TitleText>
            <BodyText variant="secondary">
              Update your personal information and contact details
            </BodyText>
          </div>

          {/* Form Card */}
          <Card className="p-6" data-testid="form-card">
            <PatientProfileForm
              mode="edit"
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              initialData={initialFormData}
              isLoading={isSubmitting}
              errors={submitErrors}
            />
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default EditPatientProfilePage;